import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'

import Navigation from '../components/navigation'
import Footer from '../components/footer'
import { usePageView } from '../hooks/useAnalytics'
import './contact.css'

const Contact = () => {
  const { t } = useTranslation('contact')

  usePageView('Contact')

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  })
  const [status, setStatus] = useState('idle') // idle, sending, success, error

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('sending')

    // Formspree form ID - получите на https://formspree.io (бесплатно до 50 сообщений/месяц)
    // Сообщения будут приходить на указанный при регистрации email (orders@orram.shop)
    const FORMSPREE_ID = process.env.REACT_APP_FORMSPREE_ID

    if (!FORMSPREE_ID) {
      // Fallback to mailto if Formspree not configured
      const mailtoSubject = encodeURIComponent(formData.subject || '[ORRAM Website] New Contact Message')
      const mailtoBody = encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
      )
      window.location.href = `mailto:orders@orram.shop?subject=${mailtoSubject}&body=${mailtoBody}`
      setTimeout(() => {
        setStatus('success')
        setFormData({ name: '', email: '', subject: '', message: '' })
      }, 500)
      return
    }

    try {
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject || 'Contact from ORRAM Website',
          message: formData.message,
          _subject: `[ORRAM Website] Message from ${formData.name}`
        })
      })

      if (response.ok) {
        setStatus('success')
        setFormData({ name: '', email: '', subject: '', message: '' })
      } else {
        setStatus('error')
      }
    } catch (error) {
      console.error('Contact form error:', error)
      setStatus('error')
    }
  }

  return (
    <div className="contact-container">
      <Helmet>
        <title>{t('meta.title')} - ORRAM Frames</title>
        <meta property="og:title" content="Contact ORRAM - Custom Golden Ratio Frames" />
        <meta name="description" content={t('meta.description')} />
      </Helmet>
      <Navigation />

      <main className="contact-main">
        {/* Hero Section */}
        <section className="contact-hero">
          <h1 className="contact-hero-title">{t('hero.title')}</h1>
          <p className="contact-hero-subtitle">{t('hero.subtitle')}</p>
        </section>

        <div className="contact-content">
          {/* Company Info */}
          <section className="contact-info">
            <h2 className="contact-info-title">{t('info.title')}</h2>

            <div className="info-block">
              <h3>{t('info.company')}</h3>
              <p className="company-name">ORRAM / Sasha de Saint Tropez</p>
              <p className="company-siret">SIRET: 379 892 433</p>
            </div>

            <div className="info-block">
              <h3>{t('info.address')}</h3>
              <p>10 place de l'Ormeau</p>
              <p>83990 Saint-Tropez</p>
              <p>FRANCE</p>
              <a
                href="https://www.google.com/maps/search/?api=1&query=10%20place%20de%20l%27Ormeau%2083990%20Saint-Tropez%20France"
                target="_blank"
                rel="noopener noreferrer"
                className="map-link"
              >
                {t('info.viewMap')} →
              </a>
            </div>

            <div className="info-block">
              <h3>{t('info.email')}</h3>
              <a href="mailto:orders@orram.shop" className="contact-link">
                orders@orram.shop
              </a>
            </div>

            <div className="info-block">
              <h3>{t('info.phone')}</h3>
              <a href="tel:+33617596447" className="contact-link">
                +33 6 17 59 64 47
              </a>
            </div>

            <div className="info-block hours">
              <h3>{t('info.hours')}</h3>
              <p>{t('info.hoursText')}</p>
            </div>
          </section>

          {/* Contact Form */}
          <section className="contact-form-section">
            <h2 className="form-title">{t('form.title')}</h2>
            <p className="form-description">{t('form.description')}</p>

            <form onSubmit={handleSubmit} className="contact-form">
              {/* Name Field */}
              <div className="form-group">
                <label htmlFor="name">
                  {t('form.name')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder={t('form.namePlaceholder')}
                />
              </div>

              {/* Email Field */}
              <div className="form-group">
                <label htmlFor="email">
                  {t('form.email')} <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={t('form.emailPlaceholder')}
                />
              </div>

              {/* Subject Field */}
              <div className="form-group">
                <label htmlFor="subject">
                  {t('form.subject')}
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder={t('form.subjectPlaceholder')}
                />
              </div>

              {/* Message Field */}
              <div className="form-group">
                <label htmlFor="message">
                  {t('form.message')} <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  placeholder={t('form.messagePlaceholder')}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="submit-button"
                disabled={status === 'sending'}
              >
                {status === 'sending'
                  ? t('form.sending')
                  : t('form.submit')
                }
              </button>

              {/* Status Messages */}
              {status === 'success' && (
                <div className="status-message success">
                  {t('form.success')}
                </div>
              )}

              {status === 'error' && (
                <div className="status-message error">
                  {t('form.error')}
                </div>
              )}
            </form>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default Contact
